import { Alert, Paper, Stack, Text } from "@mantine/core";
import { skipToken } from "@reduxjs/toolkit/query";
import React from "react";
import { useGetMcbimFloorsQuery, useGetMcbimSpacesQuery } from "../../../../../../openapi";
import { selectCurrentProjectId } from "../../../../../global/globalSliceCommon";
import { useAppDispatch, useAppSelector } from "../../../../../hooks";
import FloorplanFloorList from "./FloorplanFloorList";
import FloorplanViewer from "./FloorplanViewer";
import {
  selectDefaultMcbimFloor,
  selectMcbimFloorsArgs,
  selectSelectedFloor,
  selectUniqueMcbimFloors,
} from "./floorplanSelectors";
import { setSelectedFloorInRedux } from "./floorplanSlice";
import {
  selectMcbimHasNoSpaces,
  selectMcbimIsActivated,
} from "./mcbimSelectors";
import McbimFloorplanInfo from "./McbimFloorplanInfo";
import { selectIssueBoardId } from "../../redux/selectors/issueCommonSelectors";

const IssueFloorplan: React.FC = () => {
  const dispatch = useAppDispatch();
  const projectId = useAppSelector(selectCurrentProjectId) as string;
  const issueBoardId = useAppSelector(selectIssueBoardId);
  const floors = useAppSelector(selectUniqueMcbimFloors);
  const defaultFloor = useAppSelector(selectDefaultMcbimFloor);
  const selectedFloor = useAppSelector(selectSelectedFloor);
  const mcbimFloorsArgs = useAppSelector(selectMcbimFloorsArgs);
  const mcbimIsActivated = useAppSelector(selectMcbimIsActivated);
  const mcbimHasNoSpaces = useAppSelector(selectMcbimHasNoSpaces);
  const { data: mcbimSpaces = [], isError } = useGetMcbimSpacesQuery(
    { projectId },
    { skip: projectId == null || !mcbimIsActivated },
  );
  const { isFetching: floorsFetching, isError: floorsIsError } = useGetMcbimFloorsQuery(
    mcbimIsActivated && mcbimFloorsArgs != null ? mcbimFloorsArgs : skipToken,
  );
  React.useEffect(() => {
    if (issueBoardId == null || selectedFloor !== "" || defaultFloor === "") {
      return;
    }

    dispatch(setSelectedFloorInRedux({ issueBoardId, selectedFloor: defaultFloor }));
  }, [defaultFloor, dispatch, issueBoardId, selectedFloor]);

  return (
    <Stack gap="md" p="sm" style={{ width: "100%", height: "100%", minHeight: 0, flex: 1 }}>
      {!mcbimIsActivated && <McbimFloorplanInfo projectId={projectId} variant="notActivated" />}
      {mcbimHasNoSpaces && <McbimFloorplanInfo projectId={projectId} variant="noSpaces" />}
      {mcbimIsActivated && isError && <Alert color="red">Kunne ikke hente romdata fra MCBIM.</Alert>}
      {mcbimIsActivated && floorsIsError && <Alert color="red">Kunne ikke hente etasjer fra MCBIM.</Alert>}
      {mcbimIsActivated &&
        !isError &&
        !floorsFetching &&
        mcbimSpaces.length > 0 &&
        floors.length === 0 && (
          <Paper withBorder p="lg" radius="md">
            <Text size="sm" c="dimmed">
              Ingen etasjer tilgjengelig ennå.
            </Text>
          </Paper>
        )}
      {mcbimIsActivated && floors.length > 0 && (
        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: "grid",
            gridTemplateColumns: "280px minmax(0, 1fr)",
            gap: "1rem",
          }}
        >
          <FloorplanFloorList />
          <FloorplanViewer />
        </div>
      )}
    </Stack>
  );
};

export default IssueFloorplan;
